import React, { useState, useEffect } from 'react';
import { ChevronDown, FileText, Shield, Users, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';

export default function DerrDigitalAGB({ darkMode = false }) {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState({});

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const toggle = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  const icons = [FileText, Users, Settings, Shield, Shield, Shield, Shield, Shield, FileText];

  const agbSections = [
    { id: 'sec1', titleKey: 'agb.sec1Title', contentKey: 'agb.sec1Content' },
    { id: 'sec2', titleKey: 'agb.sec2Title', contentKey: 'agb.sec2Content' },
    { id: 'sec3', titleKey: 'agb.sec3Title', contentKey: 'agb.sec3Content' },
    { id: 'sec4', titleKey: 'agb.sec4Title', contentKey: 'agb.sec4Content' },
    { id: 'sec5', titleKey: 'agb.sec5Title', contentKey: 'agb.sec5Content' },
    { id: 'sec6', titleKey: 'agb.sec6Title', contentKey: 'agb.sec6Content' },
    { id: 'sec7', titleKey: 'agb.sec7Title', contentKey: 'agb.sec7Content' },
    { id: 'sec8', titleKey: 'agb.sec8Title', contentKey: 'agb.sec8Content' },
    { id: 'sec9', titleKey: 'agb.sec9Title', contentKey: 'agb.sec9Content' },
  ];

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>

      {/* Hero */}
      <section style={{ position: 'relative', paddingTop: 'clamp(90px, 13vh, 120px)', paddingBottom: 48, overflow: 'hidden', textAlign: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.12) 0%, transparent 65%)' : 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(99,102,241,0.07) 0%, transparent 65%)' }} />
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Rechtliches</div>
          <h1 style={{ fontSize: 'clamp(24px, 4vw, 48px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 10 }}>
            <span style={{ background: `linear-gradient(135deg, ${accent}, ${accent2})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              {t('agb.title')}
            </span>
          </h1>
          <p style={{ fontSize: 14, color: textSecondary, lineHeight: 1.65, marginBottom: 20 }}>{t('agb.subtitle')}</p>
          <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '14px 18px', textAlign: 'left', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <FileText size={14} color={accent} />
            <span style={{ fontSize: 13, color: textSecondary }}>{t('agb.validFrom')}</span>
          </div>
        </div>
      </section>

      {/* Sections */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {agbSections.map(({ id, titleKey, contentKey }, i) => {
          const isOpen = !!openSections[id];
          const Icon = icons[i] || Shield;
          return (
            <div key={id} style={{ background: cardBg, border: `1px solid ${isOpen ? accent + '40' : cardBorder}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
              <button onClick={() => toggle(id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: isOpen ? accent + '18' : (darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'), display: 'flex', alignItems: 'center', justifyContent: 'center', color: isOpen ? accent : textSecondary, flexShrink: 0, transition: 'all 0.2s' }}>
                    <Icon size={14} strokeWidth={1.8} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: isOpen ? textPrimary : textSecondary, transition: 'color 0.2s' }}>{t(titleKey)}</span>
                </div>
                <ChevronDown size={15} color={textSecondary} style={{ flexShrink: 0, transition: 'transform 0.3s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {isOpen && (
                <div style={{ padding: '14px 18px 16px', borderTop: `1px solid ${cardBorder}` }}>
                  <p style={{ fontSize: 13, color: textSecondary, lineHeight: 1.7, whiteSpace: 'pre-line', margin: 0 }}>{t(contentKey)}</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Contact info */}
        <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 14, padding: '20px 22px', marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>{t('agb.contactTitle')}</div>
            <div style={{ fontSize: 13, color: textSecondary, lineHeight: 1.7 }}>
              <strong style={{ color: textPrimary }}>DerrDigital</strong><br />
              Alte Molkerei 4<br />49692 Cappeln (Oldenburg)<br />Deutschland
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: textPrimary, marginBottom: 8 }}>{t('agb.contactDetailsTitle')}</div>
            <div style={{ fontSize: 13, color: textSecondary, lineHeight: 1.7 }}>
              E-Mail: info@derrdigital.de<br />
              Telefon: +49 (0) 174 1628004<br />
              Web: www.derrdigital.de
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <section style={{ padding: 'clamp(36px, 5vh, 56px) 0', marginTop: 56, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ maxWidth: 560, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <h2 style={{ fontSize: 'clamp(16px, 2vw, 24px)', fontWeight: 900, color: '#fff', marginBottom: 8 }}>{t('agb.ctaTitle')}</h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginBottom: 20 }}>{t('agb.ctaDesc')}</p>
          <Link to="/kontakt" style={{ display: 'inline-flex', alignItems: 'center', padding: '11px 26px', borderRadius: 11, background: '#fff', color: accent, fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>
            {t('agb.ctaButton')}
          </Link>
        </div>
      </section>
    </div>
  );
}
